import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useRouter } from "next/router";
import {
  Box,
  Center,
  Divider,
  Flex,
  Icon,
  ListItem,
  OrderedList,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  UnorderedList,
  forwardRef,
  useBreakpointValue,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import type {
  BoxProps,
  ListItemProps,
  ListProps,
  SystemStyleObject,
  TableBodyProps,
  TableCellProps,
  TableColumnHeaderProps,
  TableFooterProps,
  TableHeadProps,
  TableProps,
  TableRowProps,
} from "@chakra-ui/react";
import { RiArrowUpSFill } from "react-icons/ri";
import { getIndexArray } from "utils/array";
import type { SortOrder } from "types/global-types";

interface ITableContext {
  isFirstColumnSticky: boolean;
  stickyCellStyles?: SystemStyleObject;
}

const TableContext = createContext<ITableContext>({
  isFirstColumnSticky: false,
});

interface TableProviderProps {
  children: ReactNode;
  isFirstColumnSticky?: boolean;
}

const TableProvider = ({
  children,
  isFirstColumnSticky = false,
}: TableProviderProps) => {
  const position = useBreakpointValue(["static", "sticky"]);

  const contextValue = useMemo(
    () => ({
      isFirstColumnSticky,
      stickyCellStyles: {
        borderRightWidth: "1px",
        position,
        left: 0,
        bg: "white",
        zIndex: 1,
      },
    }),
    [isFirstColumnSticky, position]
  );

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  );
};

const useTable = () => useContext(TableContext);

interface CardTableProps extends TableProps {
  // Stick the first column of the table to the front as row headers during horizontal scrolling
  isFirstColumnSticky?: boolean;
  // `BoxProps` to pass to the `Box` that surrounds the table
  containerProps?: BoxProps;
}

const CardTable = forwardRef<CardTableProps, "div">(
  ({ children, isFirstColumnSticky, containerProps = {}, ...props }, ref) => (
    <TableProvider isFirstColumnSticky={isFirstColumnSticky}>
      <Box
        ref={ref}
        overflowX="auto"
        mb={-4}
        mx={-4}
        pb={2}
        {...containerProps}
      >
        <Table
          role="table"
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
            ...(isFirstColumnSticky && {}),
            contain: "strict",
          }}
          {...props}
        >
          {children}
        </Table>
      </Box>
    </TableProvider>
  )
);

const CardThead = ({ children, ...props }: TableHeadProps) => (
  <Thead
    role="rowgroup"
    {...props}
    position="sticky"
    top="0"
    bg="white"
    zIndex="1"
  >
    {children}
  </Thead>
);

const CardTbody = ({ children, ...props }: TableBodyProps) => (
  <Tbody role="rowgroup" className="card-table-body" {...props}>
    {children}
  </Tbody>
);

const CardTfoot = ({ children, ...props }: TableFooterProps) => (
  <Tfoot
    role="rowgroup"
    sx={{ tr: { th: { borderBottomWidth: "1px" } } }}
    {...props}
  >
    {children}
  </Tfoot>
);

const CardTr = ({ children, ...props }: TableRowProps) => (
  <Tr role="row" className="card-tr" {...props}>
    {children}
  </Tr>
);

export interface CardThProps extends TableColumnHeaderProps {
  isSorted?: boolean;
  sortDirection?: SortOrder;
}

const CardTh = ({
  children,
  isSorted = false,
  sortDirection = "asc",
  ...props
}: CardThProps) => {
  const { isFirstColumnSticky, stickyCellStyles } = useTable();

  return (
    <Th
      role="columnheader"
      p={2}
      minW="min-content"
      whiteSpace="nowrap"
      lineHeight={1}
      _first={{
        pl: 4,
        ...(isFirstColumnSticky && stickyCellStyles),
      }}
      _last={{
        pr: 4,
      }}
      {...props}
    >
      {isSorted ? (
        <Center display="inline-flex" role="button">
          {children}

          <Box ml={1} position="relative">
            <Icon
              position="absolute"
              top={0}
              as={RiArrowUpSFill}
              transform={`translateY(-50%) rotate(${
                sortDirection === "desc" ? "180" : "0"
              }deg)`}
              transition="0.2s"
              h={5}
              w={5}
              color="blue.500"
            />
          </Box>
        </Center>
      ) : (
        children
      )}
    </Th>
  );
};

interface CardTdProps extends TableCellProps {
  noPlaceholder?: boolean;
}

const CardTd = ({ children, noPlaceholder = false, ...props }: CardTdProps) => {
  const { isFirstColumnSticky, stickyCellStyles } = useTable();
  const { th: thStyles } = useMultiStyleConfig("Table", {});

  return (
    <Td
      role="cell"
      p={2}
      minW="min-content"
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      fontSize={["xs", "sm"]}
      _first={{
        ...(isFirstColumnSticky && {
          ...thStyles,
          ...stickyCellStyles,
        }),
        p: 2,
        pl: 4,
      }}
      _last={{
        pr: 4,
      }}
      {...props}
    >
      {children}
      {!children && !noPlaceholder && "--"}
    </Td>
  );
};

const CardTf = ({ children, ...props }: TableColumnHeaderProps) => (
  <Th
    role="columnheader"
    p={2}
    minW="min-content"
    whiteSpace="nowrap"
    textTransform="none"
    fontWeight="bold"
    fontSize="sm"
    letterSpacing="regular"
    lineHeight={5}
    _first={{
      pl: 4,
    }}
    _last={{
      pr: 4,
    }}
    {...props}
  >
    {children}
  </Th>
);

interface SkeletonRowsProps {
  /**
   * The number of columns to render skeletons for
   *
   * @defaultValue `3`
   */
  columns?: number;

  /**
   * The number of rows to render skeletons for
   *
   * @defaultValue `5`
   */
  rows?: number;
}

const SkeletonRows = ({ columns = 3, rows = 5 }: SkeletonRowsProps) => (
  <>
    {getIndexArray(rows).map((rowI) => (
      <CardTr key={`skeleton-cell-${rowI}`}>
        {getIndexArray(columns).map((cellI) => (
          <CardTd key={`skeleton-cell-${cellI}`}>
            <Skeleton h="25px" w="100%" />
          </CardTd>
        ))}
      </CardTr>
    ))}
  </>
);

interface FootnoteContextInterface {
  currentHash: string;
  columnCount: number;
}

const FootnoteContext = createContext<FootnoteContextInterface>({
  currentHash: "",
  columnCount: 0,
});

interface FootnoteProviderProps {
  children: ReactNode;
}

const FootnoteProvider = ({ children }: FootnoteProviderProps) => {
  const { asPath } = useRouter();
  const hashAndQuery = asPath.split("#")[1] || "";
  const currentHash = hashAndQuery.split("?")[0] || "";

  const columnCount = useBreakpointValue([1, 1, 2, 3]);

  const contextValue = useMemo(
    () => ({
      currentHash,
      columnCount,
    }),
    [columnCount, currentHash]
  );

  return (
    <FootnoteContext.Provider value={contextValue}>
      {children}
    </FootnoteContext.Provider>
  );
};

const useFootnotes = () => useContext(FootnoteContext);

interface CardTableFootnotesProps extends BoxProps {
  title?: string;
}

const CardTableFootnotes = ({
  title = "Footnotes",
  children,
  ...props
}: CardTableFootnotesProps) => (
  <FootnoteProvider>
    <Box mt={8} {...props}>
      <Text
        fontSize="xs"
        fontWeight="medium"
        letterSpacing="wider"
        textTransform="uppercase"
      >
        {title}
      </Text>

      <Divider
        w={8}
        mt={1}
        mb={2}
        borderBottomWidth="2px"
        borderColor="gray.400"
      />

      <Flex direction="column" rowGap={3} fontSize="sm" align="left">
        {children}
      </Flex>
    </Box>
  </FootnoteProvider>
);

const CardTableFootnoteUl = ({ children, ...props }: ListProps) => {
  const { columnCount } = useFootnotes();

  return (
    <UnorderedList
      ml={4}
      spacing={1}
      sx={{ columnCount, columnGap: "3rem" }}
      {...props}
    >
      {children}
    </UnorderedList>
  );
};

const CardTableFootnoteOl = ({ children, ...props }: ListProps) => {
  const { columnCount } = useFootnotes();

  return (
    <OrderedList
      ml={4}
      spacing={1}
      sx={{ columnCount, columnGap: "3rem" }}
      {...props}
    >
      {children}
    </OrderedList>
  );
};

const CardTableFootnote = ({ id, children, ...props }: ListItemProps) => {
  const { currentHash } = useFootnotes();

  return (
    <ListItem
      id={id}
      backgroundColor={currentHash === id ? "blue.100" : "transparent"}
      sx={{ breakInside: "avoid", pageBreakInside: "avoid" }}
      {...props}
    >
      {children}
    </ListItem>
  );
};

export {
  CardTable,
  CardThead,
  CardTbody,
  CardTfoot,
  CardTr,
  CardTh,
  CardTd,
  CardTf,
  CardTableFootnotes,
  CardTableFootnoteUl,
  CardTableFootnoteOl,
  CardTableFootnote,
  SkeletonRows,
};
